import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LeftNav from "../LeftNav";
import { authorizedHttpGet, authorizedHttpPost } from "../../api/httpCalls";
import urls from "../../api/urls.json";

const PAGE_SIZE = 25;

const Sharing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");
  const isOtpVerified = localStorage.getItem("verificationPassed");

  if (!authToken || !isOtpVerified) {
    sessionStorage.setItem("redirectUrl", location.pathname);
    navigate("/login");
  }

  const [isLoading, setIsLoading] = useState(false);
  const [sharingList, setSharingList] = useState([]);
  const [appName, setAppName] = useState(localStorage.getItem("appName"));
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSourceTab, setSelectedSourceTab] = useState("All");
  const [refQuery, setRefQuery] = useState("");
  const [page, setPage] = useState(1);
  const [sidebarWidth, setSidebarWidth] = useState(96);

  const fetchSharings = async (dateToFetch = selectedDate) => {
    setIsLoading(true);
    let pageToFetch = 1;
    let allSharingEntries = [];
    let totalEntriesForDate = 0;

    while (pageToFetch <= 1000) {
      const response = await authorizedHttpGet(urls.GET_SHARING_ENTRIES, {
        page: pageToFetch,
        items_per_page: 500,
        date: dateToFetch
      });

      if (!response.status) {
        setSharingList([]);
        setTotalCount(0);
        setIsLoading(false);
        return;
      }

      const pageEntries = response.data.sharing_list || [];
      totalEntriesForDate = Number(response.data.total_count) || 0;

      // Protect against an endpoint that ignores the page parameter.
      const existingEntryKeys = new Set(
        allSharingEntries.map((item) => item.id ?? item.ref)
      );
      const newPageEntries = pageEntries.filter(
        (item) => !existingEntryKeys.has(item.id ?? item.ref)
      );
      allSharingEntries = [...allSharingEntries, ...newPageEntries];

      if (
        pageEntries.length === 0 ||
        allSharingEntries.length >= totalEntriesForDate ||
        newPageEntries.length === 0
      ) {
        break;
      }

      pageToFetch += 1;
    }

    const uniqueSharingEntries = Array.from(
      new Map(
        allSharingEntries
          .filter((item) => item.ref)
          .map((item) => [String(item.ref).trim(), item])
      ).values()
    );

    if (uniqueSharingEntries.length > 0 || totalEntriesForDate === 0) {
      setSharingList(uniqueSharingEntries);
      setTotalCount(uniqueSharingEntries.length);
      setPage(1);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchSharings(selectedDate);
  }, [appName, selectedDate]);

  useEffect(() => {
    const getSidebarWidth = () => {
      const isMobile = window.innerWidth <= 1199;
      if (isMobile) {
        setSidebarWidth(0);
        return;
      }
      const sideNav = document.getElementById("mySidenav");
      if (!sideNav) {
        setSidebarWidth(96);
        return;
      }
      const width = Math.round(sideNav.getBoundingClientRect().width);
      setSidebarWidth(width > 0 ? width : 96);
    };

    getSidebarWidth();
    window.addEventListener("resize", getSidebarWidth);

    const sideNav = document.getElementById("mySidenav");
    let observer;
    if (sideNav && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => getSidebarWidth());
      observer.observe(sideNav);
    }

    return () => {
      window.removeEventListener("resize", getSidebarWidth);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const sourceTabOptions = useMemo(
    () => [
      "All",
      "People-Regular",
      "People-Women",
      "People-International",
      "Updates",
      "Images",
      "Autofeed-approved",
      "Autofeed-disapproved",
      "Autofeed-inappropriate"
    ],
    []
  );

  const normalizeSourceTab = (value = "") => {
    const raw = String(value).trim();
    const lower = raw.toLowerCase();
    if (lower === "updates") return "Updates";
    if (lower === "images") return "Images";
    if (lower === "people-regular") return "People-Regular";
    if (lower === "people-women") return "People-Women";
    if (lower === "people-international") return "People-International";
    if (lower === "autofeed-approved" || lower === "autofeed-approve") return "Autofeed-approved";
    if (lower === "autofeed-disapproved" || lower === "autofeed-disapprove") return "Autofeed-disapproved";
    if (
      lower === "autofeed-inappropriate" ||
      lower === "autofeed-inapropriate" ||
      lower === "autofeed-inappropriated"
    ) {
      return "Autofeed-inappropriate";
    }
    return raw;
  };

  const filteredList = useMemo(() => {
    const normalizedQuery = refQuery.trim().toLowerCase();
    return sharingList.filter((item) => {
      const sourceMatch =
        selectedSourceTab === "All" || normalizeSourceTab(item.source_tab) === selectedSourceTab;
      const refMatch = normalizedQuery.length === 0 || (item.ref || "").toLowerCase().includes(normalizedQuery);
      return sourceMatch && refMatch;
    });
  }, [sharingList, selectedSourceTab, refQuery]);

  const reviewCounts = useMemo(
    () =>
      sharingList.reduce(
        (counts, item) => {
          if (item.checked_out) {
            counts.reviewed += 1;
          } else {
            counts.pending += 1;
          }
          return counts;
        },
        { reviewed: 0, pending: 0 }
      ),
    [sharingList]
  );

  useEffect(() => {
    setPage(1);
  }, [selectedSourceTab, refQuery, selectedDate]);

  // The API may return only one page of rows while total_count represents all
  // refs for the selected date. Use the API total when no client-side filter
  // is active; filtered views must use the rows loaded into the browser.
  const hasClientFilters = selectedSourceTab !== "All" || refQuery.trim().length > 0;
  const totalEntries = hasClientFilters ? filteredList.length : Math.max(filteredList.length, totalCount);
  const totalPages = Math.max(1, Math.ceil(totalEntries / PAGE_SIZE));

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, page]);

  const handleLeftNavAppChange = (name) => {
    setAppName(name);
  };

  const handleStatusToggle = async (id, checkedOut) => {
    const previousList = sharingList;
    const updatedList = sharingList.map((item) =>
      item.id === id ? { ...item, checked_out: checkedOut } : item
    );
    setSharingList(updatedList);

    const response = await authorizedHttpPost(urls.TOGGLE_SHARING_CHECKED, {
      id,
      checked_out: checkedOut
    });

    if (!response.status) {
      setSharingList(previousList);
      alert("Unable to update status");
      return;
    }

    setSharingList((previousItems) =>
      previousItems.map((item) =>
        item.id === id
          ? {
              ...item,
              checked_out: response.data.checked_out,
              checked_by_name: response.data.checked_by_name || item.checked_by_name
            }
          : item
      )
    );
  };

  const exportCsv = () => {
    const headers = ["REF", "Reason", "Shared By", "Source Tab", "Reviewed By", "Review Status"];
    const rows = filteredList.map((item) => [
      item.ref || "",
      item.reason || "",
      item.curator_name || "",
      item.source_tab || "",
      item.checked_by_name || "",
      item.checked_out ? "Reviewed" : "Pending"
    ]);
    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `sharing_${selectedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const startIndex = filteredList.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filteredList.length);

  return (
    <div className="App">
      <LeftNav handleLeftNavAppChange={handleLeftNavAppChange} tabName={"Ref Review"} />

      <style>{`
        .sharing-layout {
          min-height: 100vh;
          background: #f4f7fb;
          padding: 18px 16px;
          box-sizing: border-box;
        }
        .sharing-container {
          width: 100%;
          max-width: none;
          margin: 0;
          display: grid;
          grid-template-rows: auto auto 1fr;
          gap: 12px;
        }
        .card {
          background: #fff;
          border: 1px solid #e5ebf2;
          border-radius: 16px;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
        }
        .filters {
          position: sticky;
          top: 12px;
          z-index: 10;
          padding: 16px;
        }
        .filter-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr auto;
          gap: 18px;
          align-items: end;
        }
        .field-label {
          display: block;
          margin-bottom: 6px;
          color: #4c6380;
          font-size: 17px;
          font-family: Gilory-Bold;
          letter-spacing: 0.2px;
        }
        .control {
          width: 100%;
          height: 52px;
          border: 1px solid #d4e0ec;
          border-radius: 12px;
          padding: 0 14px;
          font-family: Gilory-Semi;
          color: #243b53;
          font-size: 20px;
          background: #fff;
          transition: box-shadow 0.15s ease, border-color 0.15s ease;
        }
        .control:focus {
          outline: none;
          border-color: #5e8fce;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
        }
        .btn-export {
          height: 52px;
          border: 1px solid #d4e0ec;
          background: #fff;
          color: #102a43;
          border-radius: 12px;
          padding: 0 16px;
          font-family: Gilory-Bold;
          font-size: 18px;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .search-wrap {
          position: relative;
        }
        .search-clear {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          border: 1px solid #cfdceb;
          background: #f8fbff;
          color: #35506d;
          border-radius: 8px;
          height: 30px;
          padding: 0 9px;
          font-family: Gilory-Bold;
          font-size: 13px;
          cursor: pointer;
        }
        .btn-export:hover {
          border-color: #b7c9dc;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
          transform: translateY(-1px);
        }
        .table-shell {
          overflow: hidden;
          display: flex;
          flex-direction: column;
          min-height: 420px;
        }
        .table-wrap {
          overflow: auto;
          max-width: 100%;
          flex: 1;
        }
        .sharing-table {
          width: 100%;
          min-width: 920px;
          table-layout: fixed;
          border-collapse: separate;
          border-spacing: 0;
        }
        .sharing-table th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f6f9fc;
          color: #344e67;
          font-size: 15px;
          font-family: Gilory-Bold;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          text-align: left;
          padding: 16px 20px;
          border-bottom: 1px solid #e6edf5;
          text-align: left;
        }
        .sharing-table td {
          padding: 18px 20px;
          border-bottom: 1px solid #eef2f7;
          color: #263f58;
          font-family: Gilory-Semi;
          font-size: 21px;
          vertical-align: middle;
          text-align: left;
        }
        .sharing-table tr:nth-child(even) td {
          background: #fbfdff;
        }
        .sharing-table tbody tr {
          transition: background-color 0.15s ease, transform 0.15s ease;
        }
        .sharing-table tbody tr:hover td {
          background: #f4f9ff;
        }
        .chip {
          display: inline-flex;
          align-items: center;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 16px;
          line-height: 1;
          font-family: Gilory-Bold;
          border: 1px solid transparent;
        }
        .chip-source {
          background: #ecf3ff;
          color: #2f5d91;
          border-color: #d3e3fb;
        }
        .chip-reviewed {
          background: #e8f7ee;
          color: #197a44;
          border-color: #bfe8cf;
          cursor: pointer;
        }
        .chip-pending {
          background: #fff4e5;
          color: #a15b09;
          border-color: #f5d7ad;
          cursor: pointer;
        }
        .empty-state {
          display: flex;
          min-height: 240px;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          color: #6f859c;
          gap: 8px;
        }
        .skeleton-row td {
          height: 54px;
          padding: 0 16px;
        }
        .skeleton-bar {
          height: 12px;
          border-radius: 999px;
          background: linear-gradient(90deg, #edf2f8 25%, #e3ebf4 37%, #edf2f8 63%);
          background-size: 400% 100%;
          animation: shimmer 1.2s ease infinite;
        }
        @keyframes shimmer {
          0% { background-position: 100% 50%; }
          100% { background-position: 0 50%; }
        }
        .table-footer {
          border-top: 1px solid #e8eef5;
          padding: 14px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          background: #fff;
        }
        .footer-text {
          color: #4b647d;
          font-size: 17px;
          font-family: Gilory-Semi;
        }
        .pager {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .pager-btn {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          border: 1px solid #d8e3ef;
          background: #fff;
          color: #47627d;
          cursor: pointer;
          font-family: Gilory-Bold;
          transition: all 0.15s ease;
        }
        .pager-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .pager-current {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          border: 1px solid #bfd6ef;
          background: #eff6ff;
          color: #214b77;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: Gilory-Bold;
        }

        /* Sidebar polish for this screen */
        .sideNavStyles {
          background: #ffffff !important;
          border-right: 1px solid #e8edf4 !important;
          box-shadow: 6px 0 20px rgba(16, 24, 40, 0.04);
        }
        .sideNavStyles div[selected], .sideNavStyles .selected {
          background: #eef5ff !important;
          border-radius: 10px;
        }

        @media (max-width: 1199px) {
          .sharing-layout {
            margin-left: 0;
            width: 100vw;
            padding: 14px;
          }
          .filter-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
        @media (max-width: 767px) {
          .filters {
            position: static;
          }
          .filter-grid {
            grid-template-columns: 1fr;
          }
          .table-footer {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>

      <main
        className="sharing-layout"
        style={{
          marginLeft: `${sidebarWidth}px`,
          width: `calc(100vw - ${sidebarWidth}px)`
        }}
      >
        <section className="sharing-container">
          <div className="card filters">
            <div className="filter-grid">
              <div>
                <label className="field-label">Date</label>
                <input type="date" className="control" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
              </div>
              <div>
                <label className="field-label">Source Tab</label>
                <select className="control" value={selectedSourceTab} onChange={(e) => setSelectedSourceTab(e.target.value)}>
                  {sourceTabOptions.map((tab) => (
                    <option key={tab} value={tab}>
                      {tab === "All" ? "All Source Tabs" : tab}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">Search REF</label>
                <div className="search-wrap">
                  <input className="control" type="text" placeholder="Search by REF" value={refQuery} onChange={(e) => setRefQuery(e.target.value)} />
                  {refQuery && (
                    <button
                      type="button"
                      className="search-clear"
                      onClick={() => setRefQuery("")}
                      title="Clear and search next REF"
                    >
                      ✕ Clear
                    </button>
                  )}
                </div>
              </div>
              <button className="btn-export" onClick={exportCsv}>Export</button>
            </div>
          </div>

          <div className="card table-shell">
            <div className="table-wrap">
              <table className="sharing-table">
                <colgroup>
                <col style={{ width: "18%" }} />
                  <col style={{ width: "24%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "14%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>REF</th>
                    <th>Reason</th>
                    <th>Shared By</th>
                    <th>Source Tab</th>
                    <th>Reviewed By</th>
                    <th>Review Status</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <>
                      {[...Array(6)].map((_, idx) => (
                        <tr key={`sk-${idx}`} className="skeleton-row">
                          {[...Array(6)].map((__, cidx) => (
                            <td key={`skc-${idx}-${cidx}`}>
                              <div className="skeleton-bar" style={{ width: `${65 + ((idx + cidx) % 3) * 10}%` }} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  )}

                  {!isLoading && paginatedRows.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <div className="empty-state">
                          <div style={{ fontSize: "42px", lineHeight: 1 }}>🗂</div>
                          <div style={{ fontFamily: "Gilory-Bold", color: "#334e68" }}>No data to show</div>
                          <div>Try changing date or filters.</div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!isLoading &&
                    paginatedRows.map((item) => (
                      <tr key={item.id}>
                        <td style={{ fontFamily: "Gilory-Bold" }}>{item.ref}</td>
                        <td>{item.reason}</td>
                        <td>{item.curator_name}</td>
                        <td>
                          <span className="chip chip-source">{normalizeSourceTab(item.source_tab)}</span>
                        </td>
                        <td>{item.checked_by_name || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className={`chip ${item.checked_out ? "chip-reviewed" : "chip-pending"}`}
                            onClick={() => !item.checked_out && handleStatusToggle(item.id, true)}
                            disabled={item.checked_out}
                            title={item.checked_out ? "Reviewed status is locked" : "Mark as reviewed"}
                            style={
                              item.checked_out
                                ? { cursor: "not-allowed", opacity: 0.9 }
                                : undefined
                            }
                          >
                            {item.checked_out ? "Reviewed" : "Pending"}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <div className="footer-text">
                Showing {startIndex} to {endIndex} of {totalEntries} entries
                {" · "}Total on selected date: {totalCount}
                {" · "}Reviewed: {reviewCounts.reviewed}
                {" · "}Pending: {reviewCounts.pending}
              </div>
              <div className="pager">
                <button className="pager-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
                <span className="pager-current">{page}</span>
                <button className="pager-btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Sharing;
